<template>
  <header :class="[
    'w-full z-[100]',
    { 'fixed bg-black/30': isHomePage, 'relative bg-black': !isHomePage }
  ]">
    <!-- Company Banner -->
    <CompanyBanner />

    <div class="relative flex flex-col items-center px-4 py-1.5 w-full !bg-black/30"> <!-- Reduced py-3 to py-1.5 -->
      <div class="flex flex-col md:flex-row justify-between items-center w-full max-w-[1408px] px-0 md:px-8 py-1.5"> <!-- Changed px-4 to px-0 -->
        <!-- Logo and Mobile Buttons -->
        <div class="flex flex-col w-full md:w-auto">
          <!-- First row: Logo, Email, Phone and Hamburger Menu -->
          <div class="flex justify-end items-center w-full">
            <router-link to="/" class="mr-auto pl-0 md:pl-4"> <!-- Added pl-0 for mobile and pl-4 for desktop -->
              <img
                :src="logoUrl"
                alt="Logo"
                class="w-[140px] h-[80px] md:w-[180px] md:h-[103.28px] object-contain"
                @error="handleImageError"
              >
            </router-link>

            <!-- Mobile icon container -->
            <div class="flex items-center md:gap-6 gap-2 md:ml-0 md:hidden"> <!-- Fixed the class order -->
              <!-- Language Selector Mobile -->
              <div class="relative md:hidden">
                <button
                  @click="toggleLanguageDropdown"
                  class="language-selector flex items-center gap-2 bg-transparent cursor-pointer p-1"
                >
                  <span class="text-white font-archivo font-medium">{{ selectedLanguage }}</span>
                  <svg
                    class="w-4 h-4"
                    :class="{ 'transform rotate-180': isLanguageDropdownOpen }"
                    viewBox="0 0 24 24"
                    fill="#FFFFFF"
                  >
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                  </svg>
                </button>

                <!-- Dropdown Mobile -->
                <div
                  v-show="isLanguageDropdownOpen"
                  class="absolute top-full right-0 mt-1 bg-white rounded-md shadow-lg py-1 z-50 min-w-[100px]"
                >
                  <button
                    v-for="lang in availableLanguages"
                    :key="lang"
                    @click="selectLanguage(lang)"
                    class="flex items-center justify-center w-full px-4 py-2 hover:bg-gray-100 text-black"
                  >
                    {{ lang }}
                  </button>
                </div>
              </div>

              <!-- Email - redirects to the contact page -->
              <div class="relative">
                <router-link to="/contact" class="flex items-center">
                  <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <path d="M22 6l-10 7L2 6"/>
                  </svg>
                </router-link>
              </div>

              <!-- Phone - redirects to the contact page -->
              <div class="relative">
                <router-link to="/contact" class="flex items-center">
                  <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                </router-link>
              </div>

              <!-- Menu Button -->
              <button class="md:hidden flex items-center" @click="toggleMobileMenu">
                <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16m-7 6h7"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Second row: Project, Location, Cart and Sign in (mobile only) -->
          <div class="flex md:hidden justify-end items-center gap-3 mt-0.5">
            <!-- Mobile Project Selector (visible only when logged in and having projects) -->
            <ProjectSelector
              v-if="isAuthenticated"
              :is-authenticated="isAuthenticated"
              ref="projectSelectorMobile"
            />

            <!-- Mobile Location Selector (visible only when logged in) -->
            <LocationSelector
              v-if="isAuthenticated"
              :is-authenticated="isAuthenticated"
              ref="locationSelectorMobile"
            />

            <button @click="toggleCart" class="relative flex items-center">
              <div v-if="cartStore.itemCount > 0" class="absolute -top-2 -right-2 bg-empire-yellow text-black rounded-full min-w-[24px] h-6 px-1 flex items-center justify-center text-xs font-bold">
                {{ cartStore.itemCount }}
              </div>
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </button>
            <template v-if="isAuthenticated">
              <div class="relative">
                <button
                  @click="toggleUserMenu"
                  class="user-menu text-[15px] leading-7 text-white font-archivo font-medium flex items-center gap-2"
                >
                  {{ $t('header.greeting') }}{{ currentUser?.firstName ? `, ${currentUser.firstName}` : '' }}
                  <svg
                    class="w-4 h-4"
                    :class="{ 'transform rotate-180': isUserMenuOpen }"
                    viewBox="0 0 24 24"
                    fill="#FFDD00"
                  >
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                  </svg>
                </button>

                <!-- User Dropdown Menu Mobile -->
                <div
                  v-show="isUserMenuOpen"
                  class="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg py-1 z-50"
                >
                  <router-link
                    to="/my-account"
                    class="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M20 21v-2a4 4 0 01-4-4H8a4 4 0 01-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    {{ $t('header.myAccount') }}
                  </router-link>
                  <button
                    @click="handleLogout"
                    class="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                      <path d="M16 17l5-5-5-5" />
                      <path d="M21 12H9" />
                    </svg>
                    {{ $t('header.logout') }}
                  </button>
                </div>
              </div>
            </template>

            <router-link
              v-else
              to="/login"
              class="text-[15px] leading-7 text-white font-archivo font-medium"
            >
              {{ $t('header.signIn') }}
            </router-link>
          </div>
        </div>

        <!-- Desktop Navigation and Actions -->
        <div class="hidden md:flex items-center gap-6">
          <div class="flex flex-row items-center gap-8 w-[471px] h-[56px] mx-auto">
            <!-- Phone -->
            <div class="flex flex-row items-center gap-4 w-[205px] h-[56px]">
              <div class="flex justify-center items-center w-[52px] h-[53px] border-2 border-white rounded-[150px] p-[10px]">
                <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                </svg>
              </div>
              <div class="flex flex-col justify-center w-[137px] h-[56px]">
                <span class="font-archivo font-medium text-[14px] leading-6 text-empire-yellow">{{ $t('header.callUsToday') }}</span>
                <span class="font-archivo font-bold text-[19px] leading-7 text-white">{{ companyData.phone }}</span>
              </div>
            </div>

            <!-- Email -->
            <div class="flex flex-row items-center gap-4 w-[234px] h-[56px]">
              <div class="flex justify-center items-center w-[52px] h-[53px] border-2 border-white rounded-[150px] p-[10px]">
                <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <path d="M22 6l-10 7L2 6"/>
                </svg>
              </div>
              <div class="flex flex-col justify-center w-[166px] h-[56px]">
                <span class="font-archivo font-medium text-[14px] leading-6 text-empire-yellow">{{ $t('header.chatWithUs') }}</span>
                <span class="font-archivo font-bold text-[19px] leading-7 text-white"><a :href="`mailto:${companyData.email}`" class="font-archivo font-bold text-[19px] leading-7 text-white">
  {{ companyData.email }}
</a></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Lower Section - Navigation -->
      <div class="hidden md:flex flex-wrap w-full max-w-[1408px] px-4 py-1.5 border-t border-[rgba(78,78,78,0.35)] gap-y-2"> <!-- Reduced py-3 to py-1.5 -->
        <!-- Left Section: Navigation Links + Contact Us -->
        <div class="flex flex-wrap items-center gap-4 md:gap-5 flex-1 min-w-0">
          <nav class="flex flex-wrap items-center gap-4 md:gap-5">
          <router-link to="/" class="text-[15px] leading-7 text-white font-archivo font-medium">
            {{ $t('header.home') }}
          </router-link>
          <router-link to="/history" class="text-[15px] leading-7 text-white font-archivo font-medium">
            {{ $t('header.history') }}
          </router-link>
          <router-link to="/suppliers" class="text-[15px] leading-7 text-white font-archivo font-medium">
            {{ $t('header.suppliers') }}
          </router-link>
          <div class="relative category-dropdown" style="position: relative; z-index: 100;">
            <button
              class="text-[15px] leading-7 text-white font-archivo font-medium flex items-center gap-1"
              @click="toggleCategoryDropdown"
            >
              {{ $t('header.shop') }}
              <svg
                class="w-4 h-4"
                :class="{ 'transform rotate-180': showCategoryDropdown }"
                viewBox="0 0 24 24"
                fill="#FFDD00"
                @click.stop="toggleCategoryDropdown"
              >
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
              </svg>
            </button>

            <!-- Menu System - Three Parallel Menus -->
            <div class="shop-menu-container" v-if="showCategoryDropdown">
              <!-- First Level Menu (Categories) -->
              <div class="menu-level bg-black py-1 z-50 max-h-[500px] overflow-y-auto border-0 border-none" style="width: 250px; background-color: #000000;">
                <!-- Loading State -->
                <div v-if="loading" class="px-4 py-2 text-empire-yellow">
                  {{ $t('common.loading') }}...
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="px-4 py-2 text-red-500">
                  {{ $t('common.error') }}
                </div>

                <!-- Empty State -->
                <div v-else-if="categories.length === 0" class="px-4 py-2 text-empire-yellow">
                  {{ $t('common.noCategories') }}
                </div>

                <!-- Categories List -->
                <template v-else>
                  <div
                    v-for="category in categories"
                    :key="category.id"
                    class="category-item"
                    @mouseenter="setActiveCategory(category)"
                  >
                    <div
                      :class="[
                        'flex items-center justify-between px-4 py-2 cursor-pointer transition-colors duration-200 group font-medium tracking-wide whitespace-nowrap overflow-hidden truncate',
                        activeCategory && activeCategory.id === category.id ? 'bg-empire-yellow text-black' : 'text-white hover:bg-empire-yellow hover:text-black'
                      ]"
                      :title="category.name"
                      @click.stop="navigateToCategory(category.id)"
                    >
                      <!-- Category Name -->
                      {{ $filters.formatCategoryName(category.name).length > 20 ? $filters.formatCategoryName(category.name).substring(0, 20) + '...' : $filters.formatCategoryName(category.name) }}

                      <!-- Arrow Icon (only if has children) -->
                      <div
                        v-if="category.children && category.children.length > 0"
                        class="flex items-center justify-center w-6 h-6"
                      >
                        <svg
                          class="w-4 h-4 category-arrow"
                          viewBox="0 0 24 24"
                          :fill="activeCategory && activeCategory.id === category.id ? '#000000' : '#FBBD1E'"
                          style="transform: rotate(-90deg);"
                        >
                          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <!-- Second Level Menu (Subcategories) -->
              <div
                v-if="activeCategory && activeCategory.children && activeCategory.children.length > 0"
                class="menu-level bg-empire-yellow py-0 z-50 max-h-[500px] overflow-y-auto border-0 border-none outline-none rounded-none w-full"
                style="width: 250px; background-color: #FFDD00;"

              >
                <div
                  v-for="subcategory in activeCategory.children"
                  :key="subcategory.id"
                  class="subcategory-item"
                  @mouseenter="setActiveSubcategory(subcategory)"
                >
                  <div
                    :class="[
                      'flex items-center justify-between px-4 py-2 cursor-pointer group font-medium border-0 border-none outline-none whitespace-nowrap overflow-hidden truncate no-transition',
                      activeSubcategory && activeSubcategory.id === subcategory.id ? 'bg-black text-empire-yellow hover-style' : 'bg-empire-yellow text-black hover:bg-black hover:text-empire-yellow'
                    ]"
                    :title="subcategory.name"
                    @click.stop="navigateToCategory(subcategory.id)"
                  >
                    <!-- Subcategory Name -->
                    {{ $filters.formatCategoryName(subcategory.name).length > 18 ? $filters.formatCategoryName(subcategory.name).substring(0, 18) + '...' : $filters.formatCategoryName(subcategory.name) }}

                    <!-- Arrow Icon (only if has children) -->
                    <svg
                      v-if="subcategory.children && subcategory.children.length > 0"
                      class="w-4 h-4 subcategory-arrow"
                      viewBox="0 0 24 24"
                      :fill="activeSubcategory && activeSubcategory.id === subcategory.id ? '#FFDD00' : '#000000'"
                      style="transform: rotate(-90deg);"
                    >
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                  </div>
                </div>
              </div>

              <!-- Third Level Menu -->
              <div
                v-if="activeSubcategory && activeSubcategory.children && activeSubcategory.children.length > 0"
                class="menu-level bg-empire-yellow py-0 z-50 max-h-[500px] overflow-y-auto border-0 border-none outline-none rounded-none w-full"
                style="width: 250px; background-color: #FFDD00;"

              >
                <div
                  v-for="thirdLevel in activeSubcategory.children"
                  :key="thirdLevel.id"
                  class="third-level-item"
                  @click.stop="navigateToCategory(thirdLevel.id)"
                  :title="thirdLevel.name"
                >
                  {{ $filters.formatCategoryName(thirdLevel.name).length > 18 ? $filters.formatCategoryName(thirdLevel.name).substring(0, 18) + '...' : $filters.formatCategoryName(thirdLevel.name) }}
                </div>
              </div>
            </div>
          </div>
          <router-link to="/career" class="text-[15px] leading-7 text-white font-archivo font-medium">
            {{ $t('header.career') }}
          </router-link>
          </nav>

          <!-- Contact Us - Will wrap to new line when space is tight -->
          <router-link to="/contact" class="text-[15px] leading-7 text-white font-archivo font-medium whitespace-nowrap">
            {{ $t('header.contactUs') }}
          </router-link>
        </div>

        <!-- Right Section -->
        <div class="flex flex-wrap justify-end items-center gap-3 md:gap-1 flex-shrink-0">
          <!-- Language Desktop (initials only) -->
          <div class="relative hidden md:block mr-0">
            <button
              @click="toggleLanguageDropdown"
              class="language-selector flex items-center gap-1 text-[15px] leading-7 text-white font-archivo font-medium bg-transparent cursor-pointer pl-1 pr-2"
            >
              {{ selectedLanguage }}
              <svg
                class="w-3 h-3"
                :class="{ 'transform rotate-180': isLanguageDropdownOpen }"
                viewBox="0 0 24 24"
                fill="#FFDD00"
              >
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
              </svg>
            </button>

            <!-- Dropdown Desktop -->
            <div
              v-show="isLanguageDropdownOpen"
              class="absolute top-full left-0 mt-1 bg-white rounded-md shadow-lg py-1 z-50 min-w-[100px]"
            >
              <button
                v-for="lang in availableLanguages"
                :key="lang"
                @click="selectLanguage(lang)"
                class="flex items-center justify-center w-full px-4 py-2 text-black hover:bg-gray-100"
              >
                {{ lang }}
              </button>
            </div>
          </div>

          <!-- Project Selector (visible only when logged in and has projects) -->
          <ProjectSelector
            :is-authenticated="isAuthenticated"
            ref="projectSelector"
            class="mr-2"
          />

          <!-- Location Selector (visible only when logged in) -->
          <LocationSelector
            v-if="isAuthenticated"
            :is-authenticated="isAuthenticated"
            ref="locationSelector"
            class="mr-2"
          />

          <!-- Cart and Sign In -->
          <div class="flex items-center gap-4 md:gap-3">
            <button @click="toggleCart" class="relative flex items-center">
              <div v-if="cartStore.itemCount > 0" class="absolute -top-2 -right-2 bg-empire-yellow text-black rounded-full min-w-[24px] h-6 px-1 flex items-center justify-center text-xs font-bold">
                {{ cartStore.itemCount }}
              </div>
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </button>
            <template v-if="isAuthenticated">
              <div class="relative">
                <button
                  @click="toggleUserMenu"
                  class="user-menu text-[15px] leading-7 text-white font-archivo font-medium flex items-center gap-1"
                >
                  {{ $t('header.greeting') }}{{ currentUser?.firstName ? `, ${currentUser.firstName}` : '' }}
                  <svg
                    class="w-4 h-4"
                    :class="{ 'transform rotate-180': isUserMenuOpen }"
                    viewBox="0 0 24 24"
                    fill="#FFDD00"
                  >
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                  </svg>
                </button>

                <!-- User Dropdown Menu -->
                <div
                  v-show="isUserMenuOpen"
                  class="absolute left-0 mt-2 w-48 bg-black rounded-md shadow-lg py-1 z-50 max-h-[500px] overflow-y-auto"
                >
                  <router-link
                    to="/my-account"
                    class="flex items-center gap-2 w-full px-4 py-2 cursor-pointer transition-colors duration-200 group font-medium tracking-wide text-white hover:bg-empire-yellow hover:text-black"
                  >
                    <svg
                      class="w-5 h-5 transition-colors duration-200"
                      viewBox="0 0 24 24"
                      fill="#FFDD00"
                      :class="{'group-hover:fill-black': true}"
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                    {{ $t('header.myAccount') }}
                  </router-link>
                  <button
                    @click="handleLogout"
                    class="flex items-center gap-2 w-full px-4 py-2 cursor-pointer transition-colors duration-200 group font-medium tracking-wide text-white hover:bg-empire-yellow hover:text-black"
                  >
                    <svg
                      class="w-5 h-5 transition-colors duration-200"
                      viewBox="0 0 24 24"
                      fill="#FFDD00"
                      :class="{'group-hover:fill-black': true}"
                    >
                      <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                    </svg>
                    {{ $t('header.logout') }}
                  </button>
                </div>
              </div>
            </template>

            <router-link
              v-else
              to="/login"
              class="text-[15px] leading-7 text-white font-archivo font-medium flex items-center gap-1"
            >
              {{ $t('header.signIn') }}
              <svg
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="#FFFFFF"
              >
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </router-link>
          </div>



          <!-- Search - Desktop -->
          <div class="relative hidden md:block search-container">
            <div class="flex items-center justify-between px-4 py-2 w-full md:w-[300px] h-[42px] border-2 border-white rounded-full bg-opacity-20 backdrop-blur-sm">
              <input
                type="text"
                v-model="searchQuery"
                :placeholder="$t('header.searchPlaceholder')"
                @input="handleSearchInput"
                @keydown.down="navigateResults('down')"
                @keydown.up="navigateResults('up')"
                @keydown.enter="handleSearch"
                @keydown.esc="closeSearch"
                class="bg-transparent text-[15px] leading-7 text-white font-archivo font-medium w-full focus:outline-none placeholder-white/70"
                ref="searchInput"
              />
              <button @click="handleSearch" class="hover:opacity-80 transition-opacity">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </button>
            </div>

          </div>

          <!-- Search - Mobile -->
          <div class="md:hidden w-full px-4 mb-4 search-container">
            <div class="relative flex items-center w-full">
              <input
                type="text"
                v-model="searchQuery"
                :placeholder="$t('header.searchPlaceholder')"
                @input="handleSearchInput"
                @keydown.down="navigateResults('down')"
                @keydown.up="navigateResults('up')"
                @keydown.enter="handleSearch"
                @keydown.esc="closeSearch"
                class="w-full px-4 py-2 h-[42px] bg-transparent border-2 border-white rounded-full text-white font-archivo text-[15px] leading-7 focus:outline-none placeholder-white/70"
              />
              <button
                @click="handleSearch"
                class="absolute right-4 top-1/2 transform -translate-y-1/2 hover:opacity-80 transition-opacity"
              >
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Menu -->
      <div v-show="isMobileMenuOpen" class="md:hidden w-full">
        <!-- Navigation Links Mobile -->
        <nav class="grid grid-cols-4 gap-1 w-full py-2 border-t border-[rgba(78,78,78,0.35)]">
          <router-link to="/" class="px-1 py-1 text-[13px] leading-5 text-white font-archivo font-medium text-center">
            {{ $t('header.home') }}
          </router-link>
          <router-link to="/history" class="px-1 py-1 text-[13px] leading-5 text-white font-archivo font-medium text-center">
            {{ $t('header.history') }}
          </router-link>
          <!-- Menu Loja mobile -->
          <div class="md:hidden category-dropdown relative">
            <button
              @click="toggleCategoryDropdown"
              class="px-1 py-1 text-[13px] leading-5 text-white font-archivo font-medium text-center w-full flex items-center justify-center gap-1"
            >
              {{ $t('header.shop') }}
              <svg
                class="w-3 h-3 transition-transform duration-200"
                :class="{ 'transform rotate-180': showCategoryDropdown }"
                viewBox="0 0 24 24"
                fill="#FFDD00"
              >
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
              </svg>
            </button>

            <!-- Dropdown do menu Loja -->
            <div
              v-if="showCategoryDropdown"
              class="absolute bg-black shadow-lg z-50 rounded-lg mt-12 left-1/2 transform -translate-x-1/2 mobile-shop-menu"
              style="width: 220px;"
            >
              <div class="max-h-[40vh] overflow-y-auto py-1 scrollbar-hide" ref="categoryScrollContainer">
                <!-- First category level -->
                <div
                  v-for="category in categories"
                  :key="category.id"
                  class="relative category-item"
                >
                  <div
                    :class="['flex items-center justify-between px-3 py-1.5 cursor-pointer transition-colors duration-200 group font-medium tracking-wide mobile-category-item text-xs', category.expanded ? 'expanded' : '']"
                    :style="{
                      'background-color': category.expanded ? '#FFDD00 !important' : 'black !important',
                      'color': category.expanded ? 'black !important' : 'white !important',
                      'display': 'flex',
                      'align-items': 'center',
                      'justify-content': 'space-between',
                      'gap': '6px'
                    }"
                    @click="setActiveCategory(category)"
                  >
                    <!-- Category name -->
                    <span
                      style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; display: block;"
                      :style="{'color': category.expanded ? 'black !important' : 'white !important'}"
                      :title="category.name"
                      class="text-xs"
                    >{{ $filters.formatCategoryName(category.name) }}</span>

                    <!-- Arrow icon when it has children (rotates when expanded) -->
                    <svg
                      v-if="category.children && category.children.length > 0"
                      class="transition-transform duration-200 mobile-arrow w-3 h-3 flex-shrink-0"
                      :class="{'transform rotate-180': category.expanded}"
                      viewBox="0 0 24 24"
                      :fill="category.expanded ? '#000000' : '#FFDD00'"
                      :style="{'fill': category.expanded ? '#000000 !important' : '#FFDD00 !important'}"
                    >
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                  </div>

                  <!-- Second category level (expanded vertically) -->
                  <div v-if="category.expanded && category.children && category.children.length > 0" class="py-0" style="background-color: #FFDD00 !important;">
                    <div
                      v-for="subcategory in category.children"
                      :key="subcategory.id"
                      :class="['relative subcategory-item', subcategory.expanded ? 'expanded' : '']"
                    >
                      <div
                        :class="['flex items-center justify-between px-3 py-1.5 cursor-pointer transition-colors duration-200 group font-medium tracking-wide mobile-subcategory-item text-xs', subcategory.expanded ? 'expanded' : '']"
                        :style="{
                          'background-color': subcategory.expanded ? 'black !important' : '#FFDD00 !important',
                          'color': subcategory.expanded ? '#FFDD00 !important' : 'black !important',
                          'display': 'flex',
                          'align-items': 'center',
                          'justify-content': 'space-between',
                          'gap': '6px'
                        }"
                        @click="setActiveSubcategory(subcategory)"
                      >
                        <!-- Subcategory name -->
                        <span
                          style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; display: block;"
                          :style="{'color': subcategory.expanded ? '#FFDD00 !important' : 'black !important'}"
                          :title="subcategory.name"
                          class="text-xs"
                        >{{ $filters.formatCategoryName(subcategory.name) }}</span>

                        <!-- Arrow icon when it has children (rotates when expanded) -->
                        <svg
                          v-if="subcategory.children && subcategory.children.length > 0"
                          class="transition-transform duration-200 mobile-arrow w-3 h-3 flex-shrink-0"
                          :class="{'transform rotate-180': subcategory.expanded}"
                          viewBox="0 0 24 24"
                          :fill="subcategory.expanded ? '#FFDD00' : '#000000'"
                        >
                          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                        </svg>
                      </div>

                      <!-- Third category level (expanded vertically) -->
                      <div v-if="subcategory.expanded && subcategory.children && subcategory.children.length > 0" class="py-0 third-level-container" style="background-color: black !important;">
                        <div
                          v-for="childCategory in subcategory.children"
                          :key="childCategory.id"
                          class="px-4 py-1.5 cursor-pointer transition-colors duration-200 group font-medium tracking-wide mobile-childcategory-item text-xs"
                          style="background-color: black !important; color: #FFDD00 !important; display: flex; align-items: center;"
                          @click="navigateToCategory(childCategory.id)"
                        >
                          <span
                            style="color: #FFDD00 !important; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 160px; display: block;"
                            :title="childCategory.name"
                            class="text-xs"
                          >{{ $filters.formatCategoryName(childCategory.name) }}</span>
                        </div>
                      </div>

                      <!-- Link to the subcategory when it has no children or is not expanded -->
                      <div
                        v-if="!subcategory.children || subcategory.children.length === 0"
                        class="absolute inset-0 z-10 subcategory-link"
                        style="background-color: transparent !important;"
                        @click.stop="navigateToCategory(subcategory.id)"
                      ></div>
                    </div>
                  </div>

                  <!-- Link to the category when it has no children or is not expanded -->
                  <div
                    v-if="!category.children || category.children.length === 0"
                    class="absolute inset-0 z-10"
                    @click.stop="navigateToCategory(category.id)"
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <router-link to="/suppliers" class="px-1 py-1 text-[13px] leading-5 text-white font-archivo font-medium text-center">
            {{ $t('header.suppliers') }}
          </router-link>
          <router-link to="/career" class="px-1 py-1 text-[13px] leading-5 text-white font-archivo font-medium text-center">
            {{ $t('header.career') }}
          </router-link>
          <router-link to="/contact" class="px-1 py-1 text-[13px] leading-5 text-white font-archivo font-medium text-center">
            {{ $t('header.contactUs') }}
          </router-link>

          <!-- Mobile Search Button -->
          <div class="md:hidden col-span-4 mx-4 mt-2 search-container">
            <div class="relative flex items-center w-full">
              <input
                type="text"
                v-model="searchQuery"
                :placeholder="$t('header.searchPlaceholder')"
                @input="handleSearchInput"
                @keydown.enter="handleSearch"
                @keydown.down="navigateResults('down')"
                @keydown.up="navigateResults('up')"
                @keydown.esc="closeSearch"
                class="w-full px-4 py-2 h-[42px] bg-transparent border-2 border-white rounded-full text-white font-archivo text-[15px] leading-7 focus:outline-none"
              />
              <button
                @click="handleSearch"
                class="absolute right-4 top-1/2 transform -translate-y-1/2"
              >
                <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </button>
            </div>

            <!-- Autocomplete dropdown for mobile - COMMENTED OUT: product list removed, only Enter or clicking the search icon works -->
            <!-- <div
              v-if="showAutocomplete && filteredProducts.length > 0"
              class="absolute left-0 right-0 mt-2 mx-4 bg-white rounded-md shadow-lg z-50 max-h-[300px] overflow-y-auto search-results"
            >
              <div
                v-for="product in filteredProducts"
                :key="product.id"
                @click="selectProduct(product)"
                class="flex items-center gap-3 p-3 hover:bg-gray-100 cursor-pointer"
              >
                <img
                  :src="product.image"
                  :alt="product.name"
                  class="w-12 h-12 object-cover rounded"
                  @error="handleImageError"
                />
                <div class="flex-1 min-w-0">
                  <div class="text-black font-archivo font-bold truncate">{{ product.description }}</div>
                  <div class="text-gray-600 font-archivo font-light text-sm truncate">{{ product.name }}</div>
                  <div v-if="showPrices" class="text-gray-600 text-sm">{{ formatPrice(product.price) }}</div>
                </div>
              </div>
            </div> -->
          </div>
        </nav>
      </div>
    </div>
    <!-- Cart Widget -->
    <CartWidget v-if="cartStore.isOpen" />
  </header>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useCartStore } from '@/stores/cartStore'
import { useFinancialTogglesStore } from '@/stores/financialTogglesStore'
import CartWidget from '../cart/CartWidget.vue'
import ProjectSelector from '../project/ProjectSelector.vue'
import LocationSelector from '../location/LocationSelector.vue'
import CompanyBanner from '../company/CompanyBanner.vue'
import { categoryService } from '@/services/categoryService'
// import { productService } from '@/services/productService' // Commented out: unused after disabling the product autocomplete
import { settingsService } from '@/services/settingsService'
import { logoService } from '@/services/logoService'
import { imageService } from '@/services/imageService'
// import { debounce } from 'lodash' // Commented out: unused after disabling the product autocomplete
// import { PLACEHOLDER_IMAGE_PATH } from '@/services/imageConstants' // Commented out: unused after removing the product list
import api from '@/services/api'
import eventBus from '@/utils/eventBus'

const router = useRouter()
const route = useRoute()
const store = useStore()
const cartStore = useCartStore()
const togglesStore = useFinancialTogglesStore()
const { locale, t } = useI18n()

// Variable to control the price display
const showPrices = ref(true)
const currencySymbol = ref('$')

// Function to format a price (used in the template)
// eslint-disable-next-line no-unused-vars
const formatPrice = (price) => {
  if (price === null || price === undefined) {
    return t('products.priceToConsult')
  }
  const numericPrice = typeof price === 'string' ? parseFloat(price) : Number(price)
  // If the price is NaN or negative, show "À consultar"
  // Price 0.00 is valid and must be displayed normally
  if (isNaN(numericPrice) || numericPrice < 0) {
    return t('products.priceToConsult')
  }
  return `${currencySymbol.value}${numericPrice.toFixed(2)}`
}

// Adjust the computeds and add a ref for state control
const authState = ref({
  isAuthenticated: false,
  user: null
})

// Function to refresh the authentication state
const updateAuthState = () => {
  const token = localStorage.getItem('token')
  const userData = localStorage.getItem('user')

  authState.value = {
    isAuthenticated: !!token,
    user: userData ? JSON.parse(userData) : null
  }
}

// Computed properties atualizadas
const isAuthenticated = computed(() => authState.value.isAuthenticated)

const currentUser = computed(() => authState.value.user)

// Refs for the ProjectSelector and LocationSelector components
const projectSelector = ref(null)
const projectSelectorMobile = ref(null)
const locationSelector = ref(null)
const locationSelectorMobile = ref(null)

// Ref for the category scroll container
const categoryScrollContainer = ref(null)

// Store the original positions of the categories and subcategories
const originalCategoryPositions = ref({})
const originalSubcategoryPositions = ref({})

// Variable to store whether we are on the mobile version
const isMobile = ref(window.innerWidth < 768)

// Variable to store the scroll position before expanding/collapsing
const lastScrollPosition = ref(0)

// Estado
const isMobileMenuOpen = ref(false)
const isLanguageDropdownOpen = ref(false)
const isUserMenuOpen = ref(false)
// Checks whether the saved language is PT and, if so, switches to FR (temporarily)
const savedLanguage = localStorage.getItem('selectedLanguage')
const selectedLanguage = ref(
  savedLanguage === 'PT' ? 'FR' : (savedLanguage || 'FR')
)
const searchQuery = ref('')
const filteredProducts = ref([])
const showAutocomplete = ref(false)
const showCategoryDropdown = ref(false)
const logoUrl = ref('/images/logo/logo.png')
const categories = ref([])
const loading = ref(false)
const error = ref(null)
// const isSearching = ref(false) // Commented out: unused after disabling the product autocomplete
const selectedIndex = ref(-1)
const activeCategory = ref(null) // Active category in the menu
const activeSubcategory = ref(null) // Active subcategory in the menu

// Add this ref for the company data
const companyData = ref({
  name: '',
  email: '',
  phone: '',
  address: ''
})



// Function to load the financial settings
const loadFinancialSettings = async () => {
  try {
    const settings = await settingsService.getFinancialSettings()

    // Updates the currency symbol
    currencySymbol.value = settings.currency_symbol || '$'

    // Loads the toggle states
    togglesStore.loadTogglesFromBackend({
      currency_code_enabled: settings.currency_code_enabled,
      currency_symbol_enabled: settings.currency_symbol_enabled,
      tax_rate_enabled: settings.tax_rate_enabled,
      discount_percentage_enabled: settings.discount_percentage_enabled,
      min_order_value_enabled: settings.min_order_value_enabled,
      free_shipping_threshold_enabled: settings.free_shipping_threshold_enabled,
      shipping_cost_enabled: settings.shipping_cost_enabled,
      master_toggle_enabled: settings.master_toggle_enabled
    })

    // Updates the price visibility based on the master toggle
    showPrices.value = togglesStore.masterToggle
    console.log('[Header] Master toggle state:', togglesStore.masterToggle)
    console.log('[Header] Show prices:', showPrices.value)
  } catch (error) {
    console.error('Error loading financial settings:', error)
  }
}

// Function to load the active logo
const loadActiveLogo = async () => {
  try {
    const logo = await logoService.getActiveLogo()
    if (logo && logo.image_url) {
      logoUrl.value = logo.image_url
      console.log('[Header] Active logo loaded:', logo.name)
    }
  } catch (error) {
    console.error('Error loading active logo:', error)
    // Keeps the default logo on error
  }
}

// Temporariamente removido PT do array de idiomas disponíveis
// To re-enable it, just add 'PT' back to the array
const availableLanguages = ['FR', 'EN']

// Computed Properties
const isHomePage = computed(() => route.path === '/')

// Methods
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const handleImageError = (e) => {
  console.error('Error loading image:', e)
  // Uses the imageService utility function to handle image errors
  imageService.handleImageError(e)
}

const toggleCart = () => {
  cartStore.toggleCart()
}



const selectProduct = (product) => {
  router.push(`/product/${product.id}`)
  searchQuery.value = ''
  showAutocomplete.value = false
  if (isMobileMenuOpen.value) {
    toggleMobileMenu()
  }
}

// Method to toggle the category dropdown
const toggleCategoryDropdown = (event) => {
  event.stopPropagation()
  showCategoryDropdown.value = !showCategoryDropdown.value
  // Closes the other dropdowns when opening this one
  if (showCategoryDropdown.value) {
    isLanguageDropdownOpen.value = false
    isUserMenuOpen.value = false
    // Emits an event to close the other dropdowns
    eventBus.emit('dropdown-opened', 'category')

    // Closes the project dropdown directly
    if (projectSelector.value && typeof projectSelector.value.closeDropdown === 'function') {
      projectSelector.value.closeDropdown()
    }
    if (projectSelectorMobile.value && typeof projectSelectorMobile.value.closeDropdown === 'function') {
      projectSelectorMobile.value.closeDropdown()
    }

    // Checks whether it is the mobile version
    if (isMobile.value) {
      // Makes sure every item is collapsed when opening the menu for the first time
      categories.value.forEach(category => {
        // Resets the expansion state to false
        category.expanded = false

        // If the category has children, also reset their expansion state
        if (category.children && category.children.length > 0) {
          category.children.forEach(subcategory => {
            subcategory.expanded = false
          })
        }
      })

      // Clears the active categories
      activeCategory.value = null
      activeSubcategory.value = null

      console.log('Todos os itens do menu de Loja foram contraídos (mobile)')
    }
  } else {
    // When closing the dropdown, clear the stored original positions
    originalCategoryPositions.value = {}
    originalSubcategoryPositions.value = {}
    console.log('Posições originais das categorias e subcategorias resetadas')
  }
}



// Method for category navigation
const navigateToCategory = (categoryId) => {
  router.push(`/categories/${categoryId}`)
  showCategoryDropdown.value = false
  if (isMobileMenuOpen.value) {
    toggleMobileMenu()
  }
}

// Logout method
const handleLogout = async () => {
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  if (userId) {
    try {
      // Retrieves the cart data from localStorage before clearing it
      const cartData = localStorage.getItem(`cart_${userId}`);
      if (cartData) {
        const cartItems = JSON.parse(cartData);
        if (cartItems.length > 0) {
          // Syncs with the backend before clearing
          await cartStore.syncCartWithBackend();
          console.log('Cart synced before logout');
        }
      }
    } catch (error) {
      console.error('Error syncing cart before logout:', error);
    }
  }

  await store.dispatch('logout');
  isUserMenuOpen.value = false;

  // Removes the cart data from localStorage
  localStorage.removeItem(`cart_${userId}`);
  localStorage.removeItem('user');

  // Removes the selected project from sessionStorage
  sessionStorage.removeItem('selectedProject');
  console.log('Projeto removido do sessionStorage durante logout');

  // Emits an event to notify the other components
  eventBus.emit('selected-project-changed', null);
  console.log('Evento selected-project-changed emitido com null');

  // Resets the cart state
  cartStore.$reset();

  updateAuthState();
  router.replace('/');
}

const toggleUserMenu = (event) => {
  event.stopPropagation()
  isUserMenuOpen.value = !isUserMenuOpen.value
  // Closes the other dropdowns when opening this one
  if (isUserMenuOpen.value) {
    isLanguageDropdownOpen.value = false
    showCategoryDropdown.value = false
    // Emits an event to close the other dropdowns
    eventBus.emit('dropdown-opened', 'user')

    // Closes the project dropdown directly
    if (projectSelector.value && typeof projectSelector.value.closeDropdown === 'function') {
      projectSelector.value.closeDropdown()
    }
    if (projectSelectorMobile.value && typeof projectSelectorMobile.value.closeDropdown === 'function') {
      projectSelectorMobile.value.closeDropdown()
    }
  }
}

// Function to fetch categories
const fetchCategories = async () => {
  try {
    loading.value = true
    error.value = null
    // Fetch categories directly from the backend with a hierarchical structure
    // and only active categories with products
    const hierarchicalCategories = await categoryService.searchCategories('', true)

    // Use the hierarchical categories directly
    categories.value = hierarchicalCategories

    console.log(`[Header] Árvore de categorias com ${categories.value.length} categorias raiz`)
  } catch (err) {
    console.error('Error fetching categories:', err)
    error.value = 'Error loading categories'
    categories.value = []
  } finally {
    loading.value = false
  }
}

// Note: initializing the category expansion state is now done by the backend

// Function to set the active category
const setActiveCategory = (category) => {
  // Saves the current scroll position before any modification
  if (categoryScrollContainer.value) {
    lastScrollPosition.value = categoryScrollContainer.value.scrollTop
    console.log(`Posição de scroll salva: ${lastScrollPosition.value}px`)
  }

  // Checks whether the category is already active
  const isAlreadyActive = activeCategory.value && activeCategory.value.id === category.id

  // If the category is already active, toggle the expansion state
  if (isAlreadyActive && category.children && category.children.length > 0) {
    category.expanded = !category.expanded

    // When collapsing, restore the category to its original position (desktop only)
    if (!category.expanded) {
      // Keeps the category active
      activeCategory.value = category

      // We no longer need to restore the original position, since we do not move the items
      console.log(`Categoria ${category.name} contraída (sem reordenar)`)
    }

    return
  }

  // Sets the active category
  activeCategory.value = category
  activeSubcategory.value = null

  // Adds the expanded class to the active category
  if (category && category.children && category.children.length > 0) {
    // Marks the category as expanded
    category.expanded = true

    // Removes the expanded class from all the other categories
    categories.value.forEach(cat => {
      if (cat.id !== category.id) {
        cat.expanded = false
      }
    })

    // On mobile, we no longer reorder the categories when expanding
    // and we restore the scroll position to stay exactly where it was clicked
    if (isMobile.value) {
      const index = categories.value.findIndex(cat => cat.id === category.id)
      console.log(`Categoria ${category.name} expandida na posição ${index} (sem reordenar)`)

      // Restores the scroll position after Vue updates the DOM
      setTimeout(() => {
        if (categoryScrollContainer.value) {
          categoryScrollContainer.value.scrollTop = lastScrollPosition.value
          console.log(`Posição de scroll restaurada para ${lastScrollPosition.value}px após expandir/contrair`)
        }
      }, 50)
    } else {
      // On desktop, we no longer move the items to the top
      const index = categories.value.findIndex(cat => cat.id === category.id)
      if (index > 0) {
        // We only store the original position for reference
        originalCategoryPositions.value[category.id] = index
        console.log(`Posição original da categoria ${category.name} salva: ${index}`)
      }
    }
  }
}

// Function to set the active subcategory
const setActiveSubcategory = (subcategory) => {
  // Saves the current scroll position before any modification
  if (categoryScrollContainer.value) {
    lastScrollPosition.value = categoryScrollContainer.value.scrollTop
    console.log(`Posição de scroll salva (subcategoria): ${lastScrollPosition.value}px`)
  }

  // Checks whether the subcategory is already active
  const isAlreadyActive = activeSubcategory.value && activeSubcategory.value.id === subcategory.id

  // If the subcategory is already active, toggle the expansion state
  if (isAlreadyActive && subcategory.children && subcategory.children.length > 0) {
    subcategory.expanded = !subcategory.expanded

    // When collapsing, restore the subcategory to its original position (desktop only)
    if (!subcategory.expanded) {
      // Keeps the subcategory active
      activeSubcategory.value = subcategory

      // We no longer need to restore the original position, since we do not move the items
      console.log(`Subcategoria ${subcategory.name} contraída (sem reordenar)`)
    }

    return
  }

  // Sets the active subcategory
  activeSubcategory.value = subcategory

  // Adds the 'expanded-item' class to all expanded items
  if (subcategory && subcategory.children && subcategory.children.length > 0) {
    // Marks the item as expanded
    subcategory.expanded = true

    // Makes sure the other subcategories at the same level are collapsed
    if (activeCategory.value && activeCategory.value.children) {
      // On mobile, we no longer reorder the subcategories when expanding
      // and we restore the scroll position to stay exactly where it was clicked
      if (isMobile.value) {
        const index = activeCategory.value.children.findIndex(sub => sub.id === subcategory.id)
        console.log(`Subcategoria ${subcategory.name} expandida na posição ${index} (sem reordenar)`)

        // Restores the scroll position after Vue updates the DOM
        setTimeout(() => {
          if (categoryScrollContainer.value) {
            categoryScrollContainer.value.scrollTop = lastScrollPosition.value
            console.log(`Posição de scroll restaurada para ${lastScrollPosition.value}px após expandir/contrair subcategoria`)
          }
        }, 50)
      } else {
        // On desktop, we no longer move the items to the top
        const index = activeCategory.value.children.findIndex(sub => sub.id === subcategory.id)
        if (index > 0) {
          // We only store the original position for reference
          originalSubcategoryPositions.value[subcategory.id] = index
          console.log(`Posição original da subcategoria ${subcategory.name} salva: ${index}`)
        }
      }

      // Then collapse all the other subcategories
      activeCategory.value.children.forEach(sibling => {
        if (sibling.id !== subcategory.id) {
          sibling.expanded = false
        }
      })
    }
  }
}

// Functions for the language selector
const toggleLanguageDropdown = (event) => {
  event.stopPropagation()
  isLanguageDropdownOpen.value = !isLanguageDropdownOpen.value
  // Closes the other dropdowns when opening this one
  if (isLanguageDropdownOpen.value) {
    isUserMenuOpen.value = false
    showCategoryDropdown.value = false
    // Emits an event to close the other dropdowns
    eventBus.emit('dropdown-opened', 'language')

    // Closes the project dropdown directly
    if (projectSelector.value && typeof projectSelector.value.closeDropdown === 'function') {
      projectSelector.value.closeDropdown()
    }
    if (projectSelectorMobile.value && typeof projectSelectorMobile.value.closeDropdown === 'function') {
      projectSelectorMobile.value.closeDropdown()
    }
  }
}

const selectLanguage = (lang) => {
  selectedLanguage.value = lang
  isLanguageDropdownOpen.value = false
  locale.value = lang.toLowerCase()
}

// Event listener to close dropdowns when clicking outside
const handleClickOutside = (event) => {
  const languageSelector = event.target.closest('.language-selector')
  const userMenu = event.target.closest('.user-menu')
  const categoryDropdown = event.target.closest('.category-dropdown')
  const projectDropdown = event.target.closest('.project-dropdown')
  const searchContainer = event.target.closest('.search-container')
  const searchResults = event.target.closest('.search-results')

  // Closes the language and user dropdowns
  if (!languageSelector && !userMenu) {
    isLanguageDropdownOpen.value = false
    isUserMenuOpen.value = false
  }

  // Closes the category menu only when clicking outside of it
  if (!categoryDropdown) {
    if (showCategoryDropdown.value) {
      showCategoryDropdown.value = false
      // Clears the original positions when the dropdown is closed by an outside click
      originalCategoryPositions.value = {}
      originalSubcategoryPositions.value = {}
      console.log('Posições originais resetadas por clique fora do dropdown')
    }
  }

  // Closes the project dropdown in the ProjectSelector component
  if (!projectDropdown && projectSelector.value) {
    // Accesses the ProjectSelector component method to close the dropdown
    if (typeof projectSelector.value.closeDropdown === 'function') {
      projectSelector.value.closeDropdown()
    }
  }

  // Closes the search autocomplete and clears the field when clicking outside
  if (!searchContainer && !searchResults && showAutocomplete.value) {
    closeSearch()
    console.log('Campo de busca limpo e autocomplete fechado por clique fora')
  }
}

// Function to update the mobile state
const updateMobileState = () => {
  isMobile.value = window.innerWidth < 768
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside)

  // Adds a listener for window resizing
  window.addEventListener('resize', updateMobileState)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)

  // Removes the resize listener
  window.removeEventListener('resize', updateMobileState)
})

// Watchers
watch(selectedLanguage, (newValue) => {
  locale.value = newValue.toLowerCase()
  // Save the selected language in localStorage
  localStorage.setItem('selectedLanguage', newValue)
}, { immediate: true })

watch(searchQuery, () => {
  // Does not show the autocomplete while typing
  showAutocomplete.value = false
})

watch(isMobileMenuOpen, (newValue) => {
  if (!newValue) {
    showAutocomplete.value = false
    searchQuery.value = ''
    showCategoryDropdown.value = false

    // Clears the original positions when the mobile menu is closed
    originalCategoryPositions.value = {}
    originalSubcategoryPositions.value = {}
    console.log('Posições originais resetadas ao fechar menu mobile')
  }
})

// Watch to monitor authentication and update the selected project
watch(
  () => localStorage.getItem('token'),
  (newValue) => {
    // Updates the authentication state
    updateAuthState()

    // If the user just logged in (a token exists)
    if (newValue) {
      console.log('Usuário fez login, verificando projeto no sessionStorage')

      // Waits a moment to make sure the components are mounted
      setTimeout(() => {
        // Checks whether the project is selected in sessionStorage
        if (projectSelector.value && typeof projectSelector.value.checkSessionStorageProject === 'function') {
          console.log('Verificando projeto no sessionStorage após login (desktop)')
          projectSelector.value.checkSessionStorageProject()
        }

        if (projectSelectorMobile.value && typeof projectSelectorMobile.value.checkSessionStorageProject === 'function') {
          console.log('Verificando projeto no sessionStorage após login (mobile)')
          projectSelectorMobile.value.checkSessionStorageProject()
        }
      }, 1000) // Waits 1 second to make sure everything is loaded
    }
  },
  { immediate: true }
)

// Watch for changes in the store authentication state
watch(
  () => store.state.isAuthenticated,
  (newValue) => {
    // Forces a re-evaluation of the authentication state
    updateAuthState()

    // Checks the selected project when the authentication status changes
    if (newValue) {
      console.log('Estado de autenticação do store mudou para autenticado')
      setTimeout(() => {
        if (projectSelector.value && typeof projectSelector.value.checkSessionStorageProject === 'function') {
          console.log('Verificando projeto no sessionStorage após mudança de autenticação (desktop)')
          projectSelector.value.checkSessionStorageProject()
        }

        if (projectSelectorMobile.value && typeof projectSelectorMobile.value.checkSessionStorageProject === 'function') {
          console.log('Verificando projeto no sessionStorage após mudança de autenticação (mobile)')
          projectSelectorMobile.value.checkSessionStorageProject()
        }
      }, 1000)
    }
  }
)

// Lifecycle Hooks
onMounted(async () => {
  // Load the financial settings
  await loadFinancialSettings()

  // Load the active logo
  await loadActiveLogo()

  // Load the categories
  await fetchCategories()

  const handleClickOutside = (event) => {
    const languageButton = event.target.closest('.language-selector')
    const userButton = event.target.closest('.user-menu')
    const categoryButton = event.target.closest('.category-dropdown')

    if (!languageButton && !userButton) {
      isLanguageDropdownOpen.value = false
      isUserMenuOpen.value = false
    }

    // Closes the category menu only when clicking outside of it
    if (!categoryButton) {
      showCategoryDropdown.value = false
    }
  }

  document.addEventListener('click', handleClickOutside)

  // Adds a listener for the dropdown-opened event
  eventBus.on('dropdown-opened', (source) => {
    console.log('Evento dropdown-opened recebido de:', source)
    // If the event comes from the project dropdown, close the other dropdowns
    if (source === 'project') {
      isLanguageDropdownOpen.value = false
      isUserMenuOpen.value = false
      showCategoryDropdown.value = false
      // On mobile, close the location selector when opening the project selector
      if (isMobile.value && locationSelectorMobile.value) {
        locationSelectorMobile.value.closeDropdown?.()
      }
    }
    // If the event comes from the location dropdown, close the project selector on mobile
    if (source === 'location') {
      if (isMobile.value && projectSelectorMobile.value) {
        projectSelectorMobile.value.closeDropdown?.()
      }
    }
  })


})

let authIntervalId = null

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  eventBus.off('dropdown-opened')
  if (authIntervalId) clearInterval(authIntervalId)
})

// Checks the token on every component render
onMounted(() => {
  updateAuthState()

  authIntervalId = setInterval(() => {
    updateAuthState()
  }, 1000) // Checks every second


})

// Add a watcher for the route
watch(
  () => route.path,
  () => {
    updateAuthState()
  }
)

// Add a watcher for the authentication state
watch(() => authState.value.isAuthenticated, (newValue) => {
  if (newValue) {
    const userId = JSON.parse(localStorage.getItem('user'))?.id
    if (userId) {
      // VERSION v1.0: Does not clear the cart here (clearOnLoad = false)
      // This watcher is called during navigation, not only on login
      // To revert: keep it as is or pass false explicitly
      cartStore.loadCartFromStorage(userId, false)
    }
  }
})

// The searchCategories function was removed to avoid unintentionally opening the category dropdown while searching

// Function to normalize text (remove accents and special characters)
// Commented out - unused after disabling the product autocomplete
// const normalizeText = (text) => {
//   return text
//     .normalize('NFD')
//     .replace(/[\u0300-\u036f]/g, '') // Removes accents
//     .toLowerCase()
//     .replace(/[^\w\s]/g, ' ') // Replaces special characters with spaces
//     .replace(/\s+/g, ' ')     // Replaces multiple spaces with a single space
//     .trim();
// }

// searchProducts function commented out - product autocomplete disabled
// The search now only redirects to /categories?keyword=... when the user presses Enter or clicks the icon

// Product search debounce removed - autocomplete disabled
// const debouncedProductSearch = debounce(searchProducts, 300)

// Watch for searchQuery - the API product search was REMOVED
// The product autocomplete is disabled, so we no longer need to fetch products while typing.
// The search now only redirects to /categories?keyword=... when the user presses Enter or clicks the icon.
watch(searchQuery, (newValue) => {
  // If the search term is cleared, reload all the categories
  if (newValue.trim().length === 0) {
    fetchCategories()
  }
})

// Commented out: functions unused after removing the product list from the dropdown
// const formatPrice = (price) => {
//   const numPrice = Number(price)
//   return !isNaN(numPrice)
//     ? `$${numPrice.toFixed(2)}`
//     : '$0.00'
// }

// Commented out: function unused after removing the product list from the dropdown
// const escapeRegExp = (string) => {
//   return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// }

// Commented out: function unused after removing the product list from the dropdown
/* const highlightMatch = (text, query) => {
  if (!text) return '';

  // Checks whether the query is wrapped in double quotes for an exact search
  const isExactSearch = query.startsWith('"') && query.endsWith('"')
  let searchTerm = query

  if (isExactSearch) {
    // Removes the quotes for the exact search
    searchTerm = query.slice(1, -1)
  }

  // Normalizes the query for comparison
  const normalizedSearchTerm = normalizeText(searchTerm);

  // Splits the query into keywords
  const keywords = normalizedSearchTerm.split(' ').filter(k => k.length > 0);

  // For single-word searches or an exact search
  if (keywords.length === 1 || isExactSearch) {
    try {
      const pattern = escapeRegExp(isExactSearch ? normalizedSearchTerm : keywords[0]);
      const regex = new RegExp(`(${pattern})`, 'gi');
      return text.replace(regex, '<mark class="bg-yellow-200 text-black font-bold px-0.5 rounded">$1</mark>');
    } catch (e) {
      console.error('Erro ao criar regex para highlight:', e);
      return text;
    }
  } else {
    // For multi-word searches, highlight each word individually
    let highlightedText = text;

    // Sorts the keywords by length (descending) to avoid replacement issues
    keywords.sort((a, b) => b.length - a.length);

    keywords.forEach(keyword => {
      if (keyword.length >= 2) { // Ignores very short words
        try {
          const pattern = escapeRegExp(keyword);
          const regex = new RegExp(`(${pattern})`, 'gi');
          highlightedText = highlightedText.replace(regex, '<mark class="bg-yellow-200 text-black font-bold px-0.5 rounded">$1</mark>');
        } catch (e) {
          console.error('Erro ao criar regex para highlight de palavra-chave:', e);
        }
      }
    });

    return highlightedText;
  }
} */

// Add this function to navigate through the search results
const navigateResults = (direction) => {
  if (!showAutocomplete.value || !filteredProducts.value.length) return

  if (direction === 'down') {
    selectedIndex.value = Math.min(selectedIndex.value + 1, filteredProducts.value.length - 1)
  } else if (direction === 'up') {
    selectedIndex.value = Math.max(selectedIndex.value - 1, -1)
  }

  // If Enter is pressed with a selected item
  if (selectedIndex.value >= 0) {
    const selectedProduct = filteredProducts.value[selectedIndex.value]
    if (selectedProduct) {
      selectProduct(selectedProduct)
    }
  }
}

// Add this function to close the search
const closeSearch = () => {
  showAutocomplete.value = false
  searchQuery.value = ''
  selectedIndex.value = -1
}

// Function to handle the click on the search button
const handleSearch = () => {
  console.log('handleSearch chamado')
  const query = searchQuery.value.trim()

  if (query.length >= 1) {
    // Redirects to the categories page with the keyword parameter
    router.push({
      path: '/categories',
      query: { keyword: query }
    })
    closeSearch()
  }
}

// Modify the existing handleSearchInput function
// NOTE: the API product search was removed because the autocomplete is disabled.
// The search now only redirects to /categories?keyword=... when the user presses Enter or clicks the icon.
const handleSearchInput = (event) => {
  const value = event.target.value
  selectedIndex.value = -1 // Reset selection on new input
  searchQuery.value = value
  
  // Make sure the category dropdown is closed while typing
  if (value.trim().length > 0) {
    showCategoryDropdown.value = false
  }
}

// Watch to update the price visibility when the master toggle changes
watch(() => togglesStore.masterToggle, (newValue) => {
  console.log('[Header] Master toggle changed:', newValue)
  showPrices.value = newValue
})

// Add it to the existing watch
watch(showAutocomplete, (newValue) => {
  if (!newValue) {
    selectedIndex.value = -1
  } else {
    // If the autocomplete is shown, make sure the category dropdown is closed
    showCategoryDropdown.value = false
  }
})

// Add this function to load the company data
const loadCompanyData = async () => {
  try {
    const response = await api.get('/settings/company')
    companyData.value = {
      name: response.data.company_name || '',
      email: response.data.email || '',
      phone: response.data.phone || '',
      address: response.data.address || ''
    }
    console.log('Header company data updated:', companyData.value)
  } catch (err) {
    console.error('Error loading company data:', err)
  }
}

// Adicione o handler do evento
const updateCompanyDataHandler = async () => {
  console.log('Header received company data update event')
  await loadCompanyData()
}

// Atualize onMounted e onUnmounted
onMounted(() => {
  loadCompanyData()
  eventBus.on('company-data-updated', updateCompanyDataHandler)
})

onUnmounted(() => {
  eventBus.off('company-data-updated', updateCompanyDataHandler)
})
</script>

<style scoped>
/* Style specific to the expanded element */
.flex.items-center.justify-between.px-4.py-2.cursor-pointer.group.font-medium.border-0.border-none.outline-none.whitespace-nowrap.overflow-hidden.truncate.no-transition.bg-black.text-empire-yellow.hover-style,
.flex.items-center.justify-between.px-4.py-2.cursor-pointer.group.font-medium.border-0.border-none.outline-none.whitespace-nowrap.overflow-hidden.truncate.no-transition.bg-black.text-empire-yellow.hover-style * {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Hides the scrollbar but keeps the functionality */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;  /* Chrome, Safari and Opera */
}
.font-archivo-narrow {
  font-family: 'Archivo Narrow', sans-serif;
}

.font-archivo {
  font-family: 'Archivo', sans-serif;
}

.text-empire-yellow {
  color: #FFDD00;
}

select:focus {
  outline: none;
}

select:-moz-focusring {
  color: transparent;
  text-shadow: 0 0 0 #fff;
}

select option {
  background-color: #1E1E1E;
  color: white;
  padding: 8px;
}

@media (min-width: 768px) {
  select {
    min-width: 80px;
  }
}

@media (max-width: 767px) {
  select option {
    padding: 8px 24px;
    min-height: 40px;
  }
}

.flag-icon {
  font-size: 1.2rem;
  line-height: 1;
}

select {
  padding-left: 2rem;
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

@media (max-width: 768px) {
  .mobile-search {
    position: relative;
    z-index: 50;
  }
}

.mobile-search-overlay {
  backdrop-filter: blur(4px);
}

input {
  color: white;
  caret-color: white;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.user-menu {
  position: relative;
  z-index: 51;
}

.category-dropdown {
  position: relative;
}

/* Styles specific to the mobile menu */
@media (max-width: 768px) {
  .category-dropdown {
    position: relative;
    display: flex;
    justify-content: center;
  }
}

.search-container {
  position: relative;
}

/* Styles to make sure the search results float correctly */
.search-results {
  position: absolute !important;
  z-index: 1000 !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25) !important;
  max-height: 400px !important;
  overflow-y: auto !important;
  background-color: white !important;
  border-radius: 8px !important;
  width: 100% !important;
}

:deep(mark) {
  padding: 0;
  background-color: rgba(255, 221, 0, 0.3);
  border-radius: 2px;
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Scrollbar personalizada */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

/* Styles specific to the mobile menu */
.mobile-shop-menu .subcategory-item:hover > div,
.mobile-shop-menu .subcategory-item:hover > div * {
  background-color: initial !important;
  color: initial !important;
}

.mobile-shop-menu .subcategory-item div {
  transition: all 0.3s ease;
}

/* Colors for the collapsed second level */
.mobile-shop-menu .subcategory-item div:not(.bg-black) {
  background-color: #FFDD00 !important;
  color: black !important;
}

/* Colors for the expanded second level */
.mobile-shop-menu .subcategory-item div.bg-black {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Make sure the colors are applied correctly */
.mobile-shop-menu .bg-empire-yellow {
  background-color: #FFDD00 !important;
}

.mobile-shop-menu .text-black {
  color: black !important;
}

.mobile-shop-menu .bg-black {
  background-color: black !important;
}

.mobile-shop-menu .text-empire-yellow {
  color: #FFDD00 !important;
}

/* Sobrescrever estilos de hover */
.mobile-shop-menu .subcategory-item div:hover {
  background-color: initial !important;
  color: initial !important;
}

/* Disable hover styles that are causing problems */
.mobile-shop-menu .category-item:hover,
.mobile-shop-menu .subcategory-item:hover,
.mobile-shop-menu .category-item:hover > div,
.mobile-shop-menu .subcategory-item:hover > div {
  background-color: initial !important;
  color: initial !important;
}

/* Make sure the colors are applied correctly when the item is not expanded */
.mobile-shop-menu .subcategory-item div.bg-empire-yellow.text-black,
.mobile-shop-menu .subcategory-item div.bg-empire-yellow,
.mobile-shop-menu .subcategory-item div:not(.bg-black) {
  background-color: #FFDD00 !important;
  color: black !important;
}

/* Apply the colors directly to the elements */
.mobile-shop-menu .subcategory-item {
  background-color: #FFDD00 !important;
}

/* Make sure every second-level item has a yellow background and black text */
.mobile-shop-menu .subcategory-item div {
  background-color: #FFDD00 !important;
  color: black !important;
}

/* Make sure every text inside second-level items is black */
.mobile-shop-menu .subcategory-item div * {
  color: black !important;
}

/* Only expanded items or items with the bg-black class get a black background and yellow text */
.mobile-shop-menu .subcategory-item.expanded div,
.mobile-shop-menu .subcategory-item div.bg-black {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Make sure every text inside expanded items is yellow */
.mobile-shop-menu .subcategory-item.expanded div *,
.mobile-shop-menu .subcategory-item div.bg-black * {
  color: #FFDD00 !important;
}

/* Style specific to the third level */
.mobile-shop-menu .bg-black div {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Make sure every text on the third level is yellow when expanded */
.mobile-shop-menu .bg-black div * {
  color: #FFDD00 !important;
}

/* Make sure every text on the third level is black when not expanded */
.mobile-shop-menu div:not(.bg-black) div:not(.bg-black) {
  background-color: #FFDD00 !important;
  color: black !important;
}

.mobile-shop-menu div:not(.bg-black) div:not(.bg-black) * {
  color: black !important;
}

/* Make sure the arrows have the correct color */
.mobile-shop-menu .subcategory-item:not(.expanded) > div svg {
  fill: black !important;
}

.mobile-shop-menu .subcategory-item.expanded > div svg {
  fill: #FFDD00 !important;
}

/* Styles specific to the mobile menu - First level */
.mobile-shop-menu .category-item > div {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Expanded first level */
.mobile-shop-menu .category-item > div.bg-empire-yellow {
  background-color: #FFDD00 !important;
  color: black !important;
}

/* Styles specific to the mobile menu - Second level */
.mobile-shop-menu .subcategory-item > div {
  background-color: #FFDD00 !important;
  color: black !important;
}

/* Segundo nível expandido */
.mobile-shop-menu .subcategory-item.expanded > div,
.mobile-shop-menu .subcategory-item > div.bg-black {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Style specific to the third-level container */
.mobile-shop-menu .third-level-container {
  background-color: black !important;
}

.mobile-shop-menu .third-level-container div {
  background-color: black !important;
  color: #FFDD00 !important;
}

.mobile-shop-menu .third-level-container div * {
  color: #FFDD00 !important;
}

/* Style specific to the expanded class */
.mobile-shop-menu .expanded > div {
  background-color: black !important;
  color: #FFDD00 !important;
}

.mobile-shop-menu .expanded > div * {
  color: #FFDD00 !important;
}

.mobile-shop-menu .subcategory-item:not(.expanded) > div {
  background-color: #FFDD00 !important;
  color: black !important;
}

.mobile-shop-menu .subcategory-item:not(.expanded) > div * {
  color: black !important;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

/* Loading spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Modern styles for all the navigation menus */
nav a, .category-dropdown button {
  position: relative;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow: hidden;
  font-family: 'Montserrat', 'Archivo', sans-serif;
  letter-spacing: 0.02em;
}

nav a::before, .category-dropdown button::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background-color: #FFDD00;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform: translateX(-50%);
}

nav a:hover::before, nav a:focus::before, nav a:active::before,
.category-dropdown button:hover::before, .category-dropdown button:focus::before, .category-dropdown button:active::before {
  width: 80%;
}

nav a:hover, nav a:focus, nav a:active,
.category-dropdown button:hover, .category-dropdown button:focus, .category-dropdown button:active {
  text-shadow: 0 0 10px rgba(255, 221, 0, 0.7);
}

/* Style specific to the Shop menu */
.category-dropdown {
  position: relative;
}

/* Style for the side menus */
.category-item {
  position: relative;
}

/* Adjustment for the side menu positioning */
.category-dropdown > div {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 2px;
}

/* Adjustment for the submenu positioning */
.category-item {
  position: relative;
}

.category-item > div {
  position: relative;
}

/* Adjustments for hover on the side menus */
.category-dropdown .category-item:hover > div:first-child {
  background-color: #FFDD00;
  color: black;
}

.category-dropdown .category-item:hover > div:first-child svg {
  fill: black;
}

/* Adjustments for the expansion arrows */
.category-dropdown button svg.transform.rotate-180 {
  transform: rotate(90deg) !important;
}

.category-item > div svg.transform.rotate-180 {
  transform: rotate(90deg) !important;
}

/* Styles for the shop menu with three parallel levels */
.shop-menu-container {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  display: flex;
  gap: 2px;
  z-index: 100;
}

.menu-level {
  position: relative;
  height: fit-content;
}

/* Show the submenus on hover */
.category-item:hover > div.absolute {
  display: block !important;
}

/* Estilo do dropdown principal */
.category-dropdown > div {
  border-radius: 8px;
  overflow-y: auto;
  max-height: 500px;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Style for hover on the whole submenu item */
.subcategory-item {
  cursor: pointer;
  border: none !important;
  border-radius: 0 !important;
}

.subcategory-item > div {
  border: none !important;
  outline: none !important;
  border-radius: 0 !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.subcategory-item:hover > div {
  background-color: black;
  color: white !important;
  border: none !important;
  outline: none !important;
  border-radius: 0 !important;
}

.subcategory-item:hover > div * {
  color: white !important;
}

/* Completely remove all menu borders */
.menu-level,
.shop-menu-container,
.shop-menu-container > div,
.shop-menu-container * {
  border-radius: 0 !important;
  border: none !important;
  outline: none !important;
}

/* Remove the menu shadow */
.menu-level {
  box-shadow: none !important;
}

/* Make sure the menus expand on hover */
.category-item:hover,
.subcategory-item:hover {
  background-color: black !important;
}

.category-item:hover > div,
.subcategory-item:hover > div {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Style for the main menu */
.category-item:hover > div {
  background-color: #FFDD00 !important;
  color: black !important;
}

/* Styles for the menus */
.shop-menu-container {
  display: flex;
  gap: 0;
  background-color: #FFDD00 !important;
  box-shadow: none !important;
}

/* Make sure all menus have a fixed width */
.shop-menu-container > div {
  width: 250px !important;
  flex-shrink: 0;
  box-shadow: none !important;
  border: none !important;
}

/* Style for the main menu (black) */
.shop-menu-container > div:first-child {
  background-color: #000000 !important;
}

/* Style for the submenus (yellow) */
.shop-menu-container > div:not(:first-child) {
  background-color: #FFDD00 !important;
}

/* Make sure every element inside the submenus has a yellow background */
.shop-menu-container > div:not(:first-child) > div,
.shop-menu-container > div:not(:first-child) div:not(:hover) {
  background-color: #FFDD00 !important;
}

/* Style for hover on the submenu items */
.subcategory-item:hover > div,
.subcategory-item:hover > div *,
.subcategory-item:hover .whitespace-nowrap,
.subcategory-item:hover .truncate {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Style for active items in the submenus - same style as hover */
.subcategory-item.active > div,
.subcategory-item.active > div *,
.bg-black.text-empire-yellow,
.bg-black.text-empire-yellow *,
.hover-style,
.hover-style *,
div.bg-black.text-empire-yellow.hover-style,
div.bg-black.text-empire-yellow.hover-style *,
.no-transition.bg-black.text-empire-yellow,
.no-transition.bg-black.text-empire-yellow * {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Style for third-level items and beyond */
.third-level-item:hover,
.shop-menu-container > div:nth-child(n+3) div:hover,
div[class*="bg-black text-empire-yellow hover-style"] {
  background-color: black !important;
  color: #FFDD00 !important;
  border: none !important;
  box-shadow: none !important;
}

/* Style specific to the third level */
.third-level-item {
  border: none !important;
  outline: none !important;
  margin: 0 !important;
  padding: 10px 16px !important; /* Aumentamos um pouco o padding vertical */
  width: 100% !important;
  display: block !important;
  box-sizing: border-box !important;
  cursor: pointer !important;
  font-weight: 500 !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  background-color: #FFDD00 !important;
  color: black !important;
  transition: all 0.2s ease-in-out !important;
  height: 100% !important;
  min-height: 40px !important; /* Aumentamos um pouco a altura mínima */
}

.third-level-item:hover {
  background-color: black !important;
  color: #FFDD00 !important;
  padding: 10px 16px !important;
  margin: 0 !important;
  height: 100% !important;
  min-height: 40px !important; /* Aumentamos um pouco a altura mínima */
}

.third-level-item:hover *,
.shop-menu-container > div:nth-child(n+3) div:hover *,
.third-level-item:hover .whitespace-nowrap,
.third-level-item:hover .truncate,
div[class*="bg-black text-empire-yellow hover-style"] *,
div[class*="bg-black text-empire-yellow hover-style"] .whitespace-nowrap,
div[class*="bg-black text-empire-yellow hover-style"] .truncate,
div[class*="bg-black text-empire-yellow hover-style"] .flex {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Style for the arrows */
.subcategory-item:hover .subcategory-arrow,
.bg-black.text-empire-yellow svg,
.hover-style svg,
.hover-style .subcategory-arrow {
  fill: #FFDD00 !important;
}

/* Style for the arrow on the main menu hover */
.category-hover:hover .category-arrow {
  fill: #FBBD1E !important; /* Mantemos a seta amarela no hover */
}

/* Style specific to the div with truncated text */
.subcategory-item:hover .whitespace-nowrap.overflow-hidden.truncate,
.third-level-item:hover .whitespace-nowrap.overflow-hidden.truncate,
/* Style for the hover of the subitems (second and third level) */
.subcategory-item:hover div[class*="whitespace-nowrap overflow-hidden truncate"],
.third-level-item:hover div[class*="whitespace-nowrap overflow-hidden truncate"] {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Style specific to the first-level hover */
.category-item > div:hover {
  background-color: #FFDD00 !important;
  color: black !important;
}

/* Class to remove all transitions */
.no-transition,
.no-transition * {
  transition: none !important;
}

/* Style specific to the selected second-level item */
div.flex.items-center.justify-between.px-4.py-2.cursor-pointer.group.font-medium.border-0.border-none.outline-none.whitespace-nowrap.overflow-hidden.truncate.no-transition.bg-black.text-empire-yellow.hover-style {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Style for the arrow on level 2 and beyond */
.subcategory-item:hover .subcategory-arrow {
  fill: #FFDD00 !important;
}

.subcategory-item:hover .subcategory-arrow path {
  fill: #FFDD00 !important;
}

/* Style for the arrow on the first-level hover */
.category-item > div:hover svg.category-arrow {
  fill: black !important;
}

.category-item > div:hover svg.category-arrow path {
  fill: black !important;
}

/* Removed custom styles for the main menu hover */

/* Removed custom styles for the arrow */

/* Removed custom styles for the arrow and the container */



/* Make sure there are no borders on the transparent part */
.shop-menu-container::before,
.shop-menu-container::after,
.menu-level::before,
.menu-level::after {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

/* Make sure the header arrow rotates correctly */
.category-dropdown button svg.transform.rotate-180 {
  transform: rotate(180deg) !important;
}

/* Make sure the arrow points up when expanded */
.category-dropdown button svg.rotate-180 {
  transform: rotate(180deg) !important;
}

/* Make sure the mobile menu arrow points up when expanded */
.mobile-shop-menu .mobile-category-item svg.transform.rotate-180,
.mobile-shop-menu .mobile-subcategory-item svg.transform.rotate-180 {
  transform: rotate(180deg) !important;
}

/* Make sure all arrows have the same size */
.mobile-arrow,
.mobile-shop-menu svg,
.mobile-category-item svg,
.mobile-subcategory-item svg {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
  flex: 0 0 20px !important;
  box-sizing: content-box !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Make sure the first and last item fill completely */
.subcategory-item:first-child > div {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.subcategory-item:last-child > div {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

/* Make sure the third-level items fill completely */
.third-level-item {
  border-radius: 0 !important;
  margin: 0 !important;
  padding: 12px 16px !important;
}

/* Make sure the third-level container has no padding or margin */
.menu-level.bg-empire-yellow {
  padding: 0 !important;
  margin: 0 !important;
}

/* Style for the menu texts */
.category-item span,
.category-item div[class*="truncate"] {
  transition: all 0.2s ease;
  font-weight: 500;
}

/* Scrollbar style for the menu */
.category-dropdown > div::-webkit-scrollbar {
  width: 6px;
}

.category-dropdown > div::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
}

.category-dropdown > div::-webkit-scrollbar-thumb {
  background-color: rgba(255, 221, 0, 0.5);
  border-radius: 3px;
}

.category-dropdown > div:hover {
  box-shadow: 0 19px 38px rgba(0, 0, 0, 0.3), 0 15px 12px rgba(0, 0, 0, 0.22);
}

/* Style of the menu items */
.category-item > div {
  transition: all 0s !important; /* Remove the transition delay */
  position: relative;
  z-index: 1;
  overflow: hidden;
  font-family: 'Montserrat', 'Archivo', sans-serif;
  letter-spacing: 0.03em;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
}

/* Adds a subtle glow effect to the expanded item */
.category-item > div.bg-amber-600 {
  box-shadow: inset 0 0 10px rgba(255, 255, 255, 0.3);
}

/* Style for the texts in the expanded items */
.category-item > div.bg-amber-600 span,
.category-item > div.bg-amber-600 div[class*="truncate"] {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  font-weight: 600;
  letter-spacing: 0.04em;
}

/* Adds a border effect to the hovered item */
.category-item > div:hover:not(.bg-amber-600) {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3);
}

/* Transparency effect removed */

/* Keeping only the hover effect without changing the positioning */

/* Highlight effect for the selected item */
.category-item > div.bg-empire-yellow {
  position: relative;
  overflow: hidden;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
}

.category-item > div.bg-amber-600::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(237, 137, 54, 0.2), rgba(237, 137, 54, 0.4));
  z-index: -1;
}

/* Style for the expansion icons */
.category-item svg {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Remove the scale effect on hover to keep the size consistent */
.mobile-shop-menu .category-item:hover svg {
  transform: none !important;
}

/* Keep only the rotation when expanded */
.mobile-shop-menu .category-item svg.transform.rotate-180 {
  transform: rotate(180deg) !important;
}

.mobile-shop-menu .subcategory-item svg.transform.rotate-180 {
  transform: rotate(180deg) !important;
}

/* Animation for the submenus - keeping the original visibility */
.category-item > div + div {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Keeping the visual effects without affecting the positioning */

/* Style for the mobile menu */
.md\:hidden.category-dropdown button {
  background: transparent;
  border: none;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.md\:hidden.category-dropdown button:hover,
.md\:hidden.category-dropdown button:focus,
.md\:hidden.category-dropdown button:active {
  background: rgba(255, 221, 0, 0.1);
}

/* Animation for the mobile dropdown */
.md\:hidden.category-dropdown > div {
  transform-origin: top center;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);
}

/* Make sure the mobile menu container has black borders */
.mobile-shop-menu {
  border: 1px solid black !important;
  border-top: none !important;
}

/* Remove borders between the mobile menu items */
.mobile-shop-menu .mobile-category-item {
  border-bottom: 1px solid black !important;
  margin-bottom: -1px !important; /* To avoid double lines */
}

/* Styles for the mobile menu */
.mobile-category-item span {
  color: inherit !important;
}

.mobile-subcategory-item span {
  color: inherit !important;
}

.mobile-childcategory-item span {
  color: inherit !important;
}

/* Sobrescrever estilos do Tailwind */
.mobile-shop-menu .mobile-category-item {
  background-color: black !important;
  color: white !important;
  border-bottom: 1px solid black !important;
}

.mobile-shop-menu .mobile-category-item.expanded {
  background-color: #FFDD00 !important;
  color: black !important;
  border-bottom: 1px solid black !important;
}

/* Make sure the text color is applied correctly */
.mobile-shop-menu .mobile-category-item span {
  color: white !important;
}

/* Make sure the arrow color is yellow when the item is not expanded */
.mobile-shop-menu .mobile-category-item:not(.expanded) svg {
  fill: #FFDD00 !important;
  width: 20px !important;
  height: 20px !important;
}

.mobile-shop-menu .mobile-category-item.expanded span {
  color: black !important;
}

/* Make sure the arrow color is black when the item is expanded */
.mobile-shop-menu .mobile-category-item.expanded svg {
  fill: #000000 !important;
  width: 20px !important;
  height: 20px !important;
}

.mobile-shop-menu .mobile-subcategory-item {
  background-color: #FFDD00 !important;
  color: black !important;
}

.mobile-shop-menu .mobile-subcategory-item.expanded {
  background-color: black !important;
  color: #FFDD00 !important;
}

/* Make sure the text color is applied correctly */
.mobile-shop-menu .mobile-subcategory-item span {
  color: black !important;
}

/* Make sure the second-level arrows have the same size */
.mobile-shop-menu .mobile-subcategory-item svg {
  width: 20px !important;
  height: 20px !important;
}

.mobile-shop-menu .mobile-subcategory-item.expanded span {
  color: #FFDD00 !important;
}

/* Make sure second-level items without children have black text */
.mobile-shop-menu .subcategory-item:not(.expanded) div span {
  color: black !important;
}

/* Make sure second-level items without children have a yellow background and black text */
.mobile-shop-menu .subcategory-item:not(.expanded) div {
  background-color: #FFDD00 !important;
  color: black !important;
}

/* Make sure the links to subcategories without children have black text */
.mobile-shop-menu .subcategory-item div:not(.bg-black) span,
.mobile-shop-menu .subcategory-item div span,
.mobile-shop-menu .py-0[style*="background-color: #FFDD00"] .subcategory-item div span {
  color: black !important;
}

/* Force black color for every span inside subcategories */
.mobile-shop-menu .subcategory-item span {
  color: black !important;
}

/* Only when expanded should the color be yellow */
.mobile-shop-menu .subcategory-item.expanded span,
.mobile-shop-menu .subcategory-item.expanded div span {
  color: #FFDD00 !important;
}

.mobile-shop-menu .mobile-childcategory-item {
  background-color: black !important;
  color: #FFDD00 !important;
}
</style>
